import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, Button } from '@mui/material';
import KpiCard from '../ui/KpiCard';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, LineChart, Line } from 'recharts';

const backendUrl = 'http://localhost:3001/api';

const MerchantProfile: React.FC = () => {
  const { merchantId } = useParams<{ merchantId: string }>();
  const [merchant, setMerchant] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [series, setSeries] = useState<{ volumes: any[]; loans: any[] }>({ volumes: [], loans: [] });
  const [apps, setApps] = useState<any[]>([]);
  const [signals, setSignals] = useState<any>(null);

  useEffect(() => {
    if (!merchantId) return;
    
    axios.get(`${backendUrl}/merchants/${merchantId}`).then(r => setMerchant(r.data));
    axios.get(`${backendUrl}/metrics/summary`, { params: { merchantId } }).then(r => setSummary(r.data));
    axios.get(`${backendUrl}/metrics/timeseries`, { params: { merchantId } }).then(r => setSeries(r.data));
    axios.get(`${backendUrl}/applications`, { params: { merchantId } }).then(r => setApps(r.data));
    axios.get(`${backendUrl}/metrics/early-signals`, { params: { merchantId } }).then(r => setSignals(r.data));
  }, [merchantId]);

  const exportMerchantData = () => {
    const csv = [
      'Application ID,Applicant Name,Status,Amount,Submission Date,Credit Score',
      ...apps.map((app: any) => 
        `${app.id},${app.applicantName},${app.status},${app.requestedAmount},${app.submissionDate},${app.creditScore || 'N/A'}`
      )
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `merchant-${merchantId}-data.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!merchant) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 800, marginBottom: 8 }}>{merchant.name}</h2>
          <div style={{ color: '#6b7280' }}>
            ID: {merchant.id} • WAVE: {merchant.waveAccountId}
            {merchant.category && <span> • Category: {merchant.category}</span>}
          </div>
        </div>
        <Button variant="contained" onClick={exportMerchantData}>
          Export Data
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <KpiCard title="Total Volume" value={summary ? `${Number(summary.totalVolume || 0).toLocaleString('en-ZW')} ZiG` : '—'} color="#1565c0" deltaPct={summary?.deltas?.volume} />
        <KpiCard title="Transactions" value={summary ? Number(summary.totalTxn || 0) : '—'} color="#12b569" />
        <KpiCard title="Loans" value={summary ? Number(summary.totalLoans || 0) : '—'} color="#1976d2" deltaPct={summary?.deltas?.loans} />
        <KpiCard title="Approval Rate" value={summary && summary.totalLoans ? `${Math.round((summary.approvedLoans / summary.totalLoans) * 100)}%` : '—'} color="#00bcd4" />
        <KpiCard title="Early Signal" value={signals?.signal === 'alert' ? 'ALERT' : 'NORMAL'} color={signals?.signal === 'alert' ? '#ff4d4f' : '#12b569'} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Card style={{ padding: 16, flex: '1 1 500px' }}>
          <h4 style={{ marginTop: 0 }}>Daily Volume Trend</h4>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={series.volumes} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorVolMerch" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0b5cff" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#0b5cff" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis 
                  tickFormatter={(value) => {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value.toString();
                  }}
                />
                <Tooltip />
                <Area type="monotone" dataKey="volume" stroke="#0b5cff" fillOpacity={1} fill="url(#colorVolMerch)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card style={{ padding: 16, flex: '1 1 300px' }}>
          <h4 style={{ marginTop: 0 }}>Volume Drop Signal</h4>
          {signals && (
            <div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Previous 7 days:</strong> {Math.round(signals.prev7Avg || 0).toLocaleString('en-ZW')} ZiG
              </div>
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                <strong>Last 7 days:</strong> {Math.round(signals.last7Avg || 0).toLocaleString('en-ZW')} ZiG
              </div>
              <div style={{ fontSize: 14, marginBottom: 16 }}>
                <strong>Drop:</strong> {Math.round((signals.drop || 0) * 100)}%
              </div>
              <div style={{ 
                padding: 12, 
                borderRadius: 8, 
                background: signals.signal === 'alert' ? '#fef2f2' : '#f0fdf4',
                color: signals.signal === 'alert' ? '#dc2626' : '#16a34a',
                fontWeight: 700,
                textAlign: 'center'
              }}>
                {signals.signal === 'alert' ? '⚠️ ALERT' : '✅ NORMAL'}
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card style={{ padding: 16 }}>
        <h4 style={{ marginTop: 0 }}>Recent Loan Applications</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e0e7ff' }}>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Application ID</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Applicant</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Amount</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Status</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Credit Score</th>
                <th style={{ textAlign: 'left', padding: 12, fontWeight: 700 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {apps.slice(-10).map((app: any) => (
                <tr key={app.id} style={{ borderBottom: '1px solid #f0f4f8' }}>
                  <td style={{ padding: 12, fontFamily: 'monospace' }}>{app.id.slice(0, 8)}...</td>
                  <td style={{ padding: 12 }}>{app.applicantName}</td>
                  <td style={{ padding: 12 }}>{app.requestedAmount.toLocaleString('en-ZW')} ZiG</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      background: app.status === 'approved' ? '#dcfce7' : app.status === 'rejected' ? '#fef2f2' : '#fef3c7',
                      color: app.status === 'approved' ? '#16a34a' : app.status === 'rejected' ? '#dc2626' : '#d97706'
                    }}>
                      {app.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: 12 }}>{app.creditScore || 'N/A'}</td>
                  <td style={{ padding: 12 }}>{new Date(app.submissionDate).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {apps.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
              No loan applications found for this merchant.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default MerchantProfile;
